import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useInvitationActions } from "@/features/members/hooks/useInvitationActions";
import { useRequiredOrgId } from "@/hooks/useRequiredOrgId";

export const RevokeInvitationDialog = ({
  invitationId,
  onClose,
}: {
  invitationId: string | null;
  onClose: () => void;
}) => {
  const organizationId = useRequiredOrgId();
  const { revokeInvitation } = useInvitationActions();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(!!invitationId);
  }, [invitationId]);

  const handleDelete = () => {
    if (!invitationId) return;
    revokeInvitation.mutate(
      { organizationId, invitationId },
      {
        onSuccess: () => {
          onClose();
          toast.success("Invitation deleted", {
            description: "Invitation deleted successfully",
          });
        },
        onError: (error) => {
          toast.error("Error deleting invitation", {
            description: error.message,
          });
        },
      },
    );
  };

  return (
    <AlertDialog
      open={open}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Invitation</AlertDialogTitle>
          <AlertDialogDescription>
            This will permanently remove the invitation from your organization.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <p className="text-sm text-muted-foreground">
          Are you sure you want to delete this invitation? This action is
          irreversible.
        </p>
        <AlertDialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={revokeInvitation.isPending}
          >
            Confirm
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
